import React, { useState } from 'react'

// Third party
import { Search } from '@mui/icons-material'
import { cloneDeep } from 'lodash'
import { useDispatch } from 'react-redux'
import clsx from 'clsx'
import { where } from 'firebase/firestore'

import customImageComponent from '../../common/CustomImageComponent'
import MainModal from '../../modals/common/MainModal'
import ViewsLoader from '../../loader/ViewsLoader'

// Firestore
import FirestoreService from '../../../services/firestoreService'

import useToasterHelper from '../../../helpers/ToasterHelper'
import { getUserFullName } from '../../../helpers/helpers'

// Redux
import { HorseModel } from '../../../models/horse/horse.model'
import { getConvertedData } from '../../../models/interface.helper'
import { IHorseTeamInterface } from '../../../models/horse-team/horseTeam.interface'
import { HorseTeamModel } from '../../../models/horse-team/horseTeam.model'
import { IRiderTeamMemberInterface } from '../../../models/rider-team-member/riderTeamMember.interface'
import { IRiderTeamMember } from '../../../models/users/user.interface'
import { UserModel } from '../../../models/users/user.model'
import { ITeamMember } from '../../../models/users/user.interface'

import {
  selectRegisterEventData,
  setRegisterPaperworkData,
} from '../../../store/events/eventsSlice'
import { useAppSelector } from '../../../store/hooks'
import {
  getSelectedHorse,
  getSelectedHorseMapping,
  selectHorses,
  selectHorseReducer,
  setSelectedHorse,
  setSelectedHorseTeam,
  updateHorse,
} from '../../../store/horses/horseSlice'
import {
  selectHorseTeamMembers,
  selectProfileData,
  selectUserId,
  setProfileDetails,
} from '../../../store/user/userSlice'
import { selectPaperworkTabData } from '../../../store/registration/registrationSlice'

import helpers from '../../../commonHelpers/helpers'

// Constants
import { CONST } from '../../../const/const'
import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'
import { CountryList } from '../../../fakeData/countryList'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

type Props = {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    modal_type: string
    saveHandler: (value: IHorseTeamInterface[] | IRiderTeamMember[]) => void
  }
}

type IModalType = {
  HORSE: boolean
  TEAM: boolean
}

// Constants
export const ITEAMMEMBER_MODAL_TYPES = {
  HORSE: 'forHorses',
  TEAM: 'forTeam',
}

const CHECKED_ICON = 'assets/cp_icons/Ok-3.svg'

const UserProfileTeamTabAddTeamMember = ({ dataToPassOn, handleModal }: Props) => {
  const toastFunctions = useToasterHelper()
  const dispatch = useDispatch()

  const types: IModalType = {
    TEAM: dataToPassOn?.modal_type === 'forTeam',
    HORSE: dataToPassOn?.modal_type === 'forHorses',
  }

  const horseData = useAppSelector(selectHorses)
  const profileDetails = useAppSelector(selectProfileData)
  const selectedHorse = useAppSelector(getSelectedHorse)
  const horseReducer = useAppSelector(selectHorseReducer)
  const HorseTeamList = useAppSelector(selectHorseTeamMembers) as ITeamMember[]
  const paperworkTabData = useAppSelector(selectPaperworkTabData)
  const registerFormData = useAppSelector(selectRegisterEventData)
  const selectedHorseMapping = useAppSelector(getSelectedHorseMapping)
  const userId = useAppSelector(selectUserId)

  let registerDataCloned = cloneDeep(registerFormData)

  const [loading, setLoading] = useState(false)
  const [membersToRender, setMembersToRender] = useState<ITeamMember[]>([])
  const [searchValue, setSearchValue] = useState('')

  const getSameTypeUsers = (allUsers: ITeamMember[]) => {
    const roles = [CONST.UI.TEAM_MEMBERS_ROLES.TEAM_MEMBER]

    let list: (ITeamMember | IHorseTeamInterface)[] = []
    let alreadyExists = false
    let user: ITeamMember
    let usersOfSameType: ITeamMember[] = []
    let riderTeamMembers: IRiderTeamMemberInterface[] = getSignatories()

    if (types.TEAM) {
      list = profileDetails.userTeamMembers ?? []
    } else if (types.HORSE) {
      list = horseReducer.selected.team.data
    }

    if (riderTeamMembers.length) return

    allUsers.forEach((curr) => {
      alreadyExists = !!list.find((currHorseTeamMember) => {
        return curr?.memberId === currHorseTeamMember.memberId
      })

      if (curr?.memberId && !alreadyExists) {
        user = getConvertedData({
          ...curr,
          selected: false,
          memberRole: roles[0],
          signedStatus: 'Not sent',
        })

        usersOfSameType = [...usersOfSameType, user]
      }
    })

    return [...usersOfSameType]
  }

  /** Returns signatories of a subdoc  */
  const getSignatories = () => {
    let paperworkRowIndex = -1
    let ridersTeamMembers: IRiderTeamMemberInterface[] = []

    try {
      ridersTeamMembers = paperworkTabData?.[paperworkRowIndex]?.ridersTeamMembers ?? []
    } catch (error) {
      console.error(error, 'error')
    } finally {
      return ridersTeamMembers
    }
  }

  /** Filters the user in actual */
  const handleUsers = async (strToSearch: string) => {
    const search = strToSearch.toLowerCase().replace(/\s+/g, '')
    setSearchValue(strToSearch)

    try {
      if (search.length < 3) {
        setMembersToRender([])
        return
      }

      setLoading(true)

      let teamMembers: ITeamMember[] = []
      const usersSnaps = await FirestoreService.filterItems(COLLECTIONS.USERS.NAME, [
        where(COLLECTIONS.USERS.FIELDS.USER_NAME_NGRAMS.KEY, 'array-contains', search),
      ])

      usersSnaps.forEach((currDoc) => {
        const user = UserModel.fromFirestoreDoc(currDoc).toObject()
        if (user?.id === profileDetails.id) return
        teamMembers.push({
          memberCountry:
            CountryList.find((curr) => curr.value === user?.userNationality)?.label || null,
          memberId: user.id,
          memberEmail: user.userEmail,
          memberDob: user.userDOB ?? '',
          memberUserName: user.userName,
          memberAddress: user.userAddress,
          defaultRole: user.userDefaultRole ?? '',
          memberPhoneNumber: user.userPhoneNumber,
          memberProfilePicture: user.userProfilePicture || null,
          memberName: getUserFullName(user),
        })
      })

      const filteredUserList = getSameTypeUsers(teamMembers)
      const furtherFilter =
        HorseTeamList?.[0] && filteredUserList?.[0]
          ? filteredUserList.filter((data) =>
              HorseTeamList.find((horse) => horse.memberId !== data.memberId)
            )
          : filteredUserList

      setMembersToRender(furtherFilter ?? [])
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  /** Handles click on user */
  const onHandleSelectChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    team_member: ITeamMember
  ) => {
    const newCheckedUsers = membersToRender.map((currUser) => {
      if (currUser.memberId === team_member?.memberId) {
        return { ...currUser, selected: e.target.checked }
      }
      return currUser
    })
    setMembersToRender([...newCheckedUsers])
  }

  /**  Adds the selected members to redux, but not to db */
  const addTeamMembersToViewOnly = async () => {
    let mappingId = selectedHorseMapping.data?.id
    const selectedMembers = membersToRender?.filter((currUser) => currUser.selected)

    if (selectedMembers.length <= 0) {
      return toastFunctions.info({ message: MESSAGES_CONST.SELECT_TEAM_MEMBERS_TO_SAVE })
    }

    try {
      if (types.HORSE) {
        let horseTeamMembersList: IHorseTeamInterface[] = []
        let horse = new HorseModel(selectedHorse ?? undefined).toObject()
        let horseIndexInRedux = -1

        if (!mappingId || !horse.id)
          return toastFunctions.error({
            message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
          })

        horseTeamMembersList = cloneDeep(horseReducer.selected.team.data)
        selectedMembers.forEach((currTeamMember) => {
          horseTeamMembersList.push(
            getConvertedData(
              new HorseTeamModel({
                isMyHorse: true,
                horseId: horse.id ?? null,
                horseZone: horse.horseZone,
                horseName: horse.horseName ?? null,
                userId: userId,
                horseOwnerId: horse.horseOwnerId,
                horseOwnerName: horse.horseOwnerName,
                horseOwnerPicture: horse.horseOwnerPicture,
                memberId: currTeamMember.memberId,
                horseNameNGram: horse.horseNameNGram,
                memberName: currTeamMember.memberName,
                userHorseMappingId: mappingId ?? null,
                horseDiscipline: horse.horseDiscipline,
                memberRole: currTeamMember.memberRole ?? null,
                horseProfilePicture: horse.horseProfilePicture,
                memberProfilePicture: currTeamMember.memberProfilePicture,
              }).toObject()
            )
          )
        })

        horse.horseTeamMembers_ = horseTeamMembersList
        horse = getConvertedData(horse)
        horseIndexInRedux = horseData.findIndex((currHorse) => currHorse.horseId === horse.horseId)

        dispatch(setSelectedHorse(horse))
        dispatch(
          updateHorse({
            data: {
              ...horse,
              isMyHorse: userId === horse.horseOwnerId,
            },
            index: horseIndexInRedux,
          })
        )
        dispatch(setSelectedHorseTeam(horseTeamMembersList))
        dataToPassOn.saveHandler && dataToPassOn.saveHandler(horseTeamMembersList)
      } else if (types.TEAM) {
        selectedMembers.forEach((member, i) => {
          selectedMembers[i] = {
            ...member,
            connectedDate: new Date().toString(),
          }
        })
        let updatedTeamMembersList = [...(profileDetails.userTeamMembers ?? []), ...selectedMembers]

        let paperworkWithMembers = Object.entries(registerDataCloned.paperwork).reduce(
          (acc: any, current: any) => {
            let key = current[0]
            acc = {
              ...registerDataCloned.paperwork,
              [key]: [...registerDataCloned.paperwork[key], ...selectedMembers],
            }
            return acc
          },
          {}
        )
        dispatch(setRegisterPaperworkData(paperworkWithMembers))
        dispatch(setProfileDetails({ ...profileDetails, userTeamMembers: updatedTeamMembersList }))
        dataToPassOn.saveHandler && dataToPassOn.saveHandler(updatedTeamMembersList)
      }

      handleModal(false, MODAL_CONSTS.TEAM)
    } catch (error) {
      helpers.logger({
        isError: true,
        message: error,
      })
    }
  }

  return (
    <MainModal
      title="Add team member"
      show={true}
      type="TEAM"
      size="md"
      titleClassName="!font-normal"
      buttons={[
        {
          label: 'SAVE',
          bgClass: 'bg-SeabiscuitMainThemeColor',
          onClick: addTeamMembersToViewOnly,
          textClass: 'text-white',
          fullWidth: true,
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          onClick: () => handleModal(false, MODAL_CONSTS.TEAM),
          fullWidth: true,
        },
      ]}
    >
      <>
        <div className="inputSearch py-2 flex items-center sticky top-0 bg-white">
          <input
            type="text"
            onChange={(e) => handleUsers(e.target.value)}
            placeholder="Search..."
            className="rounded-full w-full border focus:border-[#D3DAEE] border-[#D3DAEE] text-SeabiscuitDark200ThemeColor focus:ring-0"
          />
          <Search className="absolute text-SeabiscuitMainThemeColor right-4" />
        </div>
        {loading ? (
          <div className="flex justify-center items-center w-full h-full">
            <ViewsLoader size="xl" color="red" />
          </div>
        ) : (
          <div className="uselListWarp mt-4 flex flex-col">
            {membersToRender && membersToRender.length > 0 ? (
              membersToRender.map((item, index) => (
                <label key={index} className="form-check mb-3 cursor-pointer">
                  <div className="user flex items-center rounded-lg py-1 w-full justify-between">
                    <div className="min-w-fit flex items-center">
                      <div className="w-[40px] h-[40px] min-w-[40px] rounded-full mr-2 overflow-hidden bg-gray-100">
                        {customImageComponent(item.memberProfilePicture, item.memberName)}
                      </div>
                      <div className="hourseDetails w-10/12">
                        <div className="hourseTitle text-SeabiscuitDark200ThemeColor">
                          <p className="text-SeabiscuitDark200ThemeColor text-lg capitalize">
                            {item?.memberName}
                          </p>
                          <p className="text-xs">@{item?.memberUserName}</p>
                        </div>
                      </div>
                    </div>

                    <img
                      src={CHECKED_ICON}
                      className={clsx('relative right-0 top-0', !item.selected && 'hidden')}
                      width={20}
                      height={20}
                      alt="checked"
                    />

                    <input
                      onChange={(e) => onHandleSelectChange(e, item)}
                      checked={item?.selected}
                      className={clsx(
                        'select-none ml-2 form-check-input appearance-none mr-[2px] h-4 w-4 border active:border-SeabiscuitMainThemeColor hover:border-SeabiscuitMainThemeColor focus:border-SeabiscuitMainThemeColor rounded-full focus:ring-0 focus:ring-offset-0 transition duration-200 align-top bg-no-repeat bg-center bg-contain float-left cursor-pointer right-0 checked:bg-SeabiscuitMainThemeColor checked:border-SeabiscuitMainThemeColor',
                        item.selected && 'hidden'
                      )}
                      type="checkbox"
                    />
                  </div>
                </label>
              ))
            ) : (
              <div className="w-full mt-2 text-center text-SeabiscuitDark200ThemeColor">
                {searchValue.length < 3
                  ? 'Please enter at least 3 letters in the search'
                  : 'No result found.'}
              </div>
            )}
          </div>
        )}
      </>
    </MainModal>
  )
}

export default UserProfileTeamTabAddTeamMember
